import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthorizationParams, useAuth0, OAuthError } from '@auth0/auth0-react';
import { kueskiAttribution, tracker } from '@kueski-dev/web-observability';
import { useConfig } from 'Contexts/Config';
import * as amplitude from '@amplitude/analytics-browser';
import { decode, encode } from 'js-base64';
import {
  sendLoginCompleted,
  sendLoginErrorCompleted,
} from '@kueski-analytics/events/login_amplitude_event';
import { AMPLITUDE_PROVIDER } from '@kueski-analytics/providers/providerInitializer';

import useUrlQuery from 'Hooks/useUrlQuery';
import useTrackStartSession from 'Hooks/useTrackStartSession';

import { createKYCApplication } from 'Redesign_Funnel/components/SignIn/StartApplication';
import { signInAuth0 } from 'Services/Auth/SignIn';
import FeatureFlagService from 'Services/FeatureFlags';
import { KUESKI_SESSION_STORAGE_KEY } from 'Src/consts/session';
import GtmEvent from 'Utils/GoogleTagManagerEvent';
import { getAppDesign, isEmbeddedAmazon, isMobileDesign } from 'Utils/mobile';
import { saveTrackFullFunnelLoginTime } from 'Utils/gtmEvents/TrackFullFunnel';
import { useUniversalSignUpPayload } from 'Redesign_Funnel/containers/pages/Landing/Landing.utils';
import { AMPLITUDE_KUESKI } from 'Src/consts/observabilityTargets';

import { UtmQueryParameter, UtmCampaign } from 'Models/Utm';
import { getLocalStorageItemWithTTL } from 'Utils/localStorageWithTTL';
import { payGTMSessionState } from 'Services/GTM/GTMSessionState';
import { useSessionReady } from 'Services/SessionReady';

import { AxiosError } from 'axios';
import { TrackerMediator } from '@kueski-analytics/utils/TrackerMediator';
import { EVENT_FEATURE_FLAGS } from '@kueski-analytics/utils/EventFeatureFlags';
import { createErrorLog, createLog } from './utils';
import { usePaymentLogin } from '../usePaymentLogin';
import { useAmazonSource } from './useAmazonSource';
import { useStoreUtm } from './useStoreUtm';
import { sanitizeExtParams } from './sanitizedParams.utils';
import { sanitizeRedirectUriParams } from './sanitizedRedirectUriParams.utils';

export const useSession = () => {
  const session = localStorage.getItem(KUESKI_SESSION_STORAGE_KEY);
  useStoreUtm();
  const { setSessionReady } = useSessionReady();

  const {
    getAccessTokenSilently, user, loginWithRedirect, logout,
  } = useAuth0();

  const userId = user?.sub?.replace('auth0|', '');
  const email = user?.email;

  const trackStartSession = useTrackStartSession();
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState<boolean | unknown>(false);
  const isEmbeddedAmzApp = isEmbeddedAmazon();
  const { push } = useHistory();
  const queryParams = useUrlQuery();
  const { payment_id: paymentId, payment_intent_id: paymentIntentId } = queryParams;

  const {
    features: {
      AMAZON_SIGNUP_TRACKING: isAmazonSignUpTrackingEnabled,
    },
  } = useConfig();
  const { getPaymentData } = usePaymentLogin();
  const signupPayload = useUniversalSignUpPayload();
  const amazonPayload = useAmazonSource();

  const loginWithAuth0 = async () => {
    const merchantData = await getPaymentData();
    const deviceId = AMPLITUDE_PROVIDER.getDeviceId();
    await kueskiAttribution.update({
      forceInvalidate: true,
      merchant_id: merchantData?.merchantId ?? '',
    });

    const { clientId, sessionId } = payGTMSessionState.session;

    const client = (clientId && sessionId) && `${clientId}+${sessionId}`;

    let identifiers = '';

    if (deviceId && client) {
      identifiers = `${deviceId}+${client}`;
    } else if (deviceId) {
      identifiers = deviceId;
    } else if (client) {
      identifiers = client;
    }

    const safeQueryParams = sanitizeRedirectUriParams(queryParams);

    const sanitizedSearch = new URLSearchParams(safeQueryParams).toString();

    const redirectUri = `${window.location.origin}/pay/${sanitizedSearch ? `?${sanitizedSearch}` : ''}`;

    const authParams: AuthorizationParams = sanitizeExtParams({
      redirectUri,
      ...merchantData,
      ...signupPayload,
      ...amazonPayload,
      'ext-deviceId': identifiers,
      'ext-payment-id': paymentId,
      'ext-payment-intent-id': paymentIntentId,
    }, {
      encodeKeys: ['ext-payment-intent-id', 'ext-payment-id', 'ext-deviceId'],
      specialTruncateKeys: ['ext-deviceId'],
    });

    createLog('useSession initialize auth0 login with redirect', {
      ...authParams,
      email,
      userId,
    });

    loginWithRedirect({
      authorizationParams: authParams,
    });
  };

  const getAuth0Token = async (): Promise<string | undefined> => {
    try {
      createLog('useSession attempt access token', {
        email,
        userId,
      });

      const tokenAuth0 = await getAccessTokenSilently({ cacheMode: 'off' });
      return tokenAuth0;
    } catch (error) {
      /**
       * TODO: validate missing_refresh_token errors
       * and handle loginWithRedirect
       */
      if (error as OAuthError) {
        createErrorLog('useSession error access token', {
          email,
          error,
          userId,
        });
      }

      if ((error as OAuthError)?.error === 'invalid_grant') {
        createErrorLog('Fatal session error (circuit breaker)', {
          email,
          error,
          userId,
        });

        logout({
          logoutParams: {
            returnTo: `${window.location.origin}/pay/error/?reason=too_many_retries`,
          },
        });
        return 'invalid_grant';
      }

      return undefined;
    }
  };

  const onSuccessLogin = async ({
    forceVerifyEmail,
    isEmailVerified,
    isKnown,
    isRecurrent,
    sessionId,
    userID,
  }: {
    forceVerifyEmail: boolean;
    isEmailVerified: boolean;
    isKnown: boolean;
    isRecurrent: boolean;
    sessionId: string;
    userID: string;
  }) => {
    if (!email) return;
    const merchantData = await getPaymentData();

    const isVerifyAccount = FeatureFlagService.isFeatureFlagEnabled('VERIFY_ACCOUNT');

    const isMobile = isMobileDesign();
    const hasPayment = paymentId || paymentIntentId;

    localStorage.setItem('email', email);
    localStorage.setItem(KUESKI_SESSION_STORAGE_KEY, sessionId);
    localStorage.setItem('hintId', encode(email));

    if (isMobile) {
      localStorage.setItem('is_known', String(isKnown));
    }

    createLog('useSession on success login', {
      email, hasPayment, isKnown, isMobile, userId: userID,
    });

    const eventData = {
      is_embedded_amz: isEmbeddedAmzApp,
      kueski_user_id: userID,
      payment_id: paymentId,
      payment_intent_id: paymentIntentId,
      recurrent: isRecurrent,
      ...(merchantData?.['ext-store-total'] && { purchase_amount: decode(merchantData['ext-store-total']).replace('$', '') }),
      ...(merchantData?.['ext-store-name'] && { merchant_name: decode(merchantData['ext-store-name']) }),
      merchant_id: merchantData?.merchantId,
    };

    amplitude.setUserId(userID);
    await kueskiAttribution.update({
      forceNotify: true,
      merchant_id: merchantData?.merchantId ?? '',
    });

    const oldEvent = () => sendLoginCompleted(eventData);
    const newEvent = () => (
      tracker.track('action', {
        attributes: { action_name: 'log_in_completed' },
        target: AMPLITUDE_KUESKI,
      })
    );

    // ToDo: this event is valid?
    GtmEvent({
      environment: getAppDesign(),
      event: 'kpay_landing_login_success',
      isUniversalLogin: true,
      origin: 'web',
      userEmail: email,
      userID,
      withPayment: hasPayment,
    });

    const mediator = new TrackerMediator(EVENT_FEATURE_FLAGS.home);
    mediator
      .addOldTracker(oldEvent)
      .addNewTracker(newEvent)
      .track();

    setSessionReady(true);

    saveTrackFullFunnelLoginTime();

    const shouldVerifyEmail = forceVerifyEmail && !isEmailVerified;
    const isForcedToVerifyEmail = shouldVerifyEmail && !isMobile && isVerifyAccount;

    const utmCampaign = getLocalStorageItemWithTTL(UtmQueryParameter.CAMPAIGN);
    const isCashInstCampaign = utmCampaign === UtmCampaign.CASH_INSTALLMENTS_LANDING;

    /**
     * Create KYC application if has not payment or isKnown false.
     * Otherwise handle status application in useAuthWithPaymentId Auth.jsx
     */
    if (hasPayment || isKnown || isCashInstCampaign) {
      createLog('useSession finish load', {
        email, hasPayment, isCashInstCampaign, isKnown, isMobile, userId: userID,
      });

      return;
    }

    createLog('useSession finish load and create kyc application', {
      email, hasPayment, isCashInstCampaign, isKnown, isMobile, userId: userID,
    });

    await createKYCApplication({
      forceVerifyEmail: isForcedToVerifyEmail,
      isAmazonSignUpTrackingEnabled,
      pushConnected: push,
      userSource: user?.user_src,
    });
  };

  const exchangeToken = async ({ accessToken }: { accessToken?: string }) => {
    try {
      const response = await signInAuth0(accessToken);
      const auth0Token = accessToken || '';

      const {
        force_verify_email: forceVerifyEmail,
        is_email_verified: isEmailVerified,
        is_known: isKnown,
        user_id: userID,
        is_recurrent: isRecurrent,
      } = response.data;

      createLog('useSession /log_in successful', {
        email, isEmailVerified, isKnown, isRecurrent, userId: userID,
      });

      await trackStartSession();

      await onSuccessLogin({
        forceVerifyEmail,
        isEmailVerified,
        isKnown,
        isRecurrent,
        sessionId: auth0Token,
        userID,
      });
      setIsLoading(false);
    } catch (error) {
      const oldEvent = () => sendLoginErrorCompleted({ error_message: error as string });
      const newEvent = () => (
        tracker.track('action', {
          attributes: { action_name: 'error_log_in_completed' },
          customAttributes: { error_message: error as string },
          target: AMPLITUDE_KUESKI,
        })
      );
      const mediator = new TrackerMediator(EVENT_FEATURE_FLAGS.home);
      mediator
        .addOldTracker(oldEvent)
        .addNewTracker(newEvent)
        .track();
      setLoginError(error);
      setIsLoading(false);

      const axiosError = error as AxiosError;
      if (axiosError?.response) {
        createErrorLog('Error Sign In from Auth0 flow /universal_auth/log_in', {
          email,
          error,
          userId,
        });
      }
    }
  };

  async function loginFunnel() {
    const accessToken = await getAuth0Token();

    if (accessToken === 'invalid_grant') {
      return;
    }

    if (accessToken === undefined) await loginWithAuth0();
    else await exchangeToken({ accessToken });
  }

  return {
    email,
    isLoading,
    loginError,
    loginFunnel,
    loginWithAuth0,
    session,
    userId,
  };
};
